import React, { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import {
  getContractStatus,
  getMyContract,
  getContractByAdminToken,
  getAuthToken,
  clearAuthToken,
} from "../../services";
import PhoneVerification from "./PhoneVerification";
import ContractView from "./ContractView";
import SuccessScreen from "./SuccessScreen";
import ExpiredScreen from "./ExpiredScreen";
import { Navigation } from "../../components/navigation";
import { Button } from "../../styles/SharedStyles";
import { TEXTS } from "../../constants";
import {
  SignPageWrapper,
  PageContent,
  DecorativeShape,
  LoadingContainer,
  SpinnerLarge,
  LoadingText,
  ErrorCard,
  ErrorIcon,
  SuccessCard,
  SuccessIcon,
} from "./SignPage.styles";

// Decorative background shapes component
const BackgroundDecorations = () => (
  <>
    <DecorativeShape className="shape1" />
    <DecorativeShape className="shape2" />
    <DecorativeShape className="shape3" />
    <DecorativeShape className="shape4" />
  </>
);

// Page states
const STATES = {
  LOADING: "loading",
  VERIFY_PHONE: "verify_phone",
  VIEW_CONTRACT: "view_contract",
  SUCCESS: "success",
  EXPIRED: "expired",
  ALREADY_SIGNED: "already_signed",
  ERROR: "error",
};

function SignPage() {
  const { token } = useParams();
  const [pageState, setPageState] = useState(STATES.LOADING);
  const [contractStatus, setContractStatus] = useState(null);
  const [contractData, setContractData] = useState(null);
  const [error, setError] = useState(null);
  const [signedData, setSignedData] = useState(null);

  // Check contract status
  const checkStatus = useCallback(async () => {
    try {
      setPageState(STATES.LOADING);
      const response = await getContractStatus(token);

      if (!response.data.exists) {
        setError({ message: TEXTS.CONTRACT.ERRORS.NOT_FOUND });
        setPageState(STATES.ERROR);
        return;
      }

      setContractStatus(response.data);

      if (response.data.status === "EXPIRED" || response.data.isExpired) {
        setPageState(STATES.EXPIRED);
        return;
      }

      if (response.data.status === "SIGNED" || response.data.isSigned) {
        setPageState(STATES.ALREADY_SIGNED);
        return;
      }

      // Admin view link: load content without OTP (read-only)
      if (response.data.isAdminView) {
        const contractResponse = await getContractByAdminToken(token);
        setContractData(contractResponse.data);
        setPageState(STATES.VIEW_CONTRACT);
        return;
      }

      // Check if we have a valid JWT token
      const authToken = getAuthToken();
      if (authToken) {
        try {
          const contractResponse = await getMyContract();
          setContractData(contractResponse.data);
          setPageState(STATES.VIEW_CONTRACT);
          return;
        } catch (err) {
          // Token expired or invalid, clear it
          clearAuthToken();
        }
      }

      // Need phone verification
      setPageState(STATES.VERIFY_PHONE);
    } catch (err) {
      console.error("Status check error:", err);

      if (err.code === "CONTRACT_EXPIRED" || err.code === "EXPIRED") {
        setPageState(STATES.EXPIRED);
        return;
      }

      setError(err);
      setPageState(STATES.ERROR);
    }
  }, [token]);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  // Handle successful OTP verification
  const handleVerified = async () => {
    try {
      const contractResponse = await getMyContract();
      setContractData(contractResponse.data);
      setPageState(STATES.VIEW_CONTRACT);
    } catch (err) {
      setError(err);
      setPageState(STATES.ERROR);
    }
  };

  // Handle successful signing
  const handleSigned = (data) => {
    setSignedData(data);
    setPageState(STATES.SUCCESS);
  };

  // Render loading state
  if (pageState === STATES.LOADING) {
    return (
      <>
        <Navigation showBack={true} />
        <SignPageWrapper>
          <BackgroundDecorations />
          <PageContent>
            <LoadingContainer>
              <SpinnerLarge />
              <LoadingText>{TEXTS.COMMON.LOADING}</LoadingText>
            </LoadingContainer>
          </PageContent>
        </SignPageWrapper>
      </>
    );
  }

  // Render error state
  if (pageState === STATES.ERROR) {
    return (
      <>
        <Navigation showBack={true} />
        <SignPageWrapper>
          <BackgroundDecorations />
          <PageContent>
            <ErrorCard>
              <ErrorIcon>!</ErrorIcon>
              <h2 style={{ marginBottom: "12px" }}>{TEXTS.COMMON.ERROR}</h2>
              <p style={{ color: "#64748B", marginBottom: "24px" }}>
                {error?.message || TEXTS.CONTRACT.ERRORS.LOAD_FAILED}
              </p>
              <Button
                variant="primary"
                onClick={() => window.location.reload()}
              >
                {TEXTS.COMMON.TRY_AGAIN}
              </Button>
            </ErrorCard>
          </PageContent>
        </SignPageWrapper>
      </>
    );
  }

  // Render expired state
  if (pageState === STATES.EXPIRED) {
    return (
      <>
        <Navigation showBack={true} />
        <ExpiredScreen clientName={contractStatus?.clientName} />
      </>
    );
  }

  // Render already signed state
  if (pageState === STATES.ALREADY_SIGNED) {
    return (
      <>
        <Navigation showBack={true} />
        <SignPageWrapper>
          <BackgroundDecorations />
          <PageContent>
            <SuccessCard>
              <SuccessIcon>✓</SuccessIcon>
              <h2 style={{ marginBottom: "12px", color: "#059669" }}>
                {TEXTS.ALREADY_SIGNED.TITLE}
              </h2>
              <p style={{ color: "#64748B", margin: 0 }}>
                {TEXTS.ALREADY_SIGNED.MESSAGE}
              </p>
            </SuccessCard>
          </PageContent>
        </SignPageWrapper>
      </>
    );
  }

  // Render phone verification
  if (pageState === STATES.VERIFY_PHONE) {
    return (
      <>
        <Navigation showBack={true} />
        <SignPageWrapper>
          <BackgroundDecorations />
          <PageContent>
            <PhoneVerification
              token={token}
              clientName={contractStatus?.clientName}
              clientPhoneMasked={contractStatus?.clientPhoneMasked}
              onVerified={handleVerified}
            />
          </PageContent>
        </SignPageWrapper>
      </>
    );
  }

  // Render contract view
  if (pageState === STATES.VIEW_CONTRACT) {
    return (
      <>
        <Navigation showBack={true} />
        <SignPageWrapper>
          <BackgroundDecorations />
          <PageContent>
            <ContractView
              contract={contractData}
              onSigned={handleSigned}
              readOnly={contractData?.isAdminView}
            />
          </PageContent>
        </SignPageWrapper>
      </>
    );
  }

  // Render success screen
  if (pageState === STATES.SUCCESS) {
    return (
      <>
        <Navigation showBack={false} showDocuments={false} />
        <SuccessScreen
          signedData={signedData}
          clientName={contractData?.clientName}
        />
      </>
    );
  }

  return null;
}

export default SignPage;
